import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Home, ArrowLeft, Shield } from 'lucide-react';
import { useAuth } from './AuthBridge';
import { useThemeTokens } from '../hooks/useActiveTheme';

interface NestingDetail {
  currentPageKey: string;
  level2Label: string | null;
  onBackAction?: string | null;
}

export const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeCert } = useAuth();
  const tokens = useThemeTokens();
  const { certId } = useParams<{ certId?: string }>();
  
  // Local state to hold the page's deep level navigation
  const [nesting, setNesting] = useState<NestingDetail | null>(null);

  useEffect(() => {
    // Reset nesting state on every route transition
    setNesting(null);

    const handleNestingUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<NestingDetail>;
      setNesting(customEvent.detail || null);
    };

    window.addEventListener('update-breadcrumbs-nesting', handleNestingUpdate);
    return () => {
      window.removeEventListener('update-breadcrumbs-nesting', handleNestingUpdate);
    };
  }, [location.pathname]);

  // Split path parts
  const pathParts = location.pathname.split('/').filter(Boolean);
  const isManage = pathParts[0] === 'manage';
  const isPractice = pathParts[0] === 'practice' || pathParts[0] === 'practice-exams';
  const pageKey = isManage ? pathParts[1] : (isPractice ? (pathParts[2] || 'dashboard') : (pathParts[1] || 'dashboard'));

  // Human-friendly mapping for primary categories
  const getPageTitle = (key: string): string => {
    switch (key) {
      // User Pages
      case 'dashboard': return 'Dashboard';
      case 'programs': return 'Program Blueprint & Guide';
      case 'overview': return 'Program Overview';
      case 'details': return 'Program Details';
      case 'guide': return 'Study Guide';
      case 'flashcards': return 'Flashcards';
      case 'skills': return 'Skills Laboratory';
      case 'simulator': return 'Clinical Math Lab';
      case 'podcasts': return 'Audio Podcasts';
      case 'exam': return 'Board Simulation';
      case 'scenario': return 'Situational Scenarios';
      case 'search': return 'Study Hub Search';
      case 'profile': return 'Candidate Profile';
      case 'elite': return 'Elite Pro Upgrade';
      case 'help': return 'Operational Manual';
      case 'practice': return 'Practice Quiz';
      case 'history': return 'Exam History';
      case 'tutor': return 'Clinical Study Coach';
      case 'missed': return 'Missed Questions Review';
      case 'faculty': return 'Board Faculty';

      // Admin Pages
      case 'admin-dashboard': return 'Admin Dashboard';
      case 'admin-analytics': return 'Suite Analytics';
      case 'admin-faculty': return 'Faculty Manager';
      case 'admin-certifications': return 'Certifications Manager';
      case 'admin-factory': return 'Admin AI Factory';
      case 'admin-prompts': return 'Prompt Console';
      case 'admin-questions': return 'Question Manager';
      case 'admin-flashcards': return 'Flashcards Manager';
      case 'admin-users': return 'User Account Manager';
      case 'admin-config': return 'Exam Configurations';
      case 'admin-sponsored': return 'Sponsor & Dynamic Ads';
      case 'admin-promos': return 'Promo Codes Manager';
      case 'admin-study-content': return 'Study Guide Syllabus';
      case 'admin-skills-content': return 'Procedural Skills Syllabus';
      case 'admin-simulator': return 'Math Scenario Manager';
      case 'admin-podcasts': return 'Syllabus Podcasts';
      case 'admin-ai-status': return 'API Diagnostic Status';
      case 'admin-notifications': return 'System Notifications';
      case 'admin-database': return 'Durable Database Console';
      case 'admin-seo': return 'Search Engine Optimization';
      case 'admin-scenarios': return 'Situational Scenarios syllabus';
      case 'admin-feedback': return 'Student Feedback Logs';
      default:
        return key.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
  };

  const handlePrimaryClick = () => {
    if (isManage) {
      navigate('/manage/admin-dashboard');
    } else {
      const activeCertId = activeCert?.id || 'cna';
      navigate(`/practice/${activeCertId}/dashboard`);
    }
  };

  const handleBasePageClick = () => {
    if (isManage) {
      navigate(`/manage/${pageKey}`);
    } else {
      const activeCertId = activeCert?.id || 'cna';
      navigate(`/practice/${activeCertId}/${pageKey}`);
    }
  };

  // Back action helper when deep level is active
  const triggerBackNavigation = () => {
    if (nesting && nesting.onBackAction) {
      window.dispatchEvent(new CustomEvent(nesting.onBackAction));
    } else {
      handleBasePageClick();
    }
  };

  // Skip showing if we are at root login page or public program guide pages
  if (
    pathParts.length === 0 || 
    location.pathname === '/login' ||
    location.pathname.startsWith('/program') ||
    location.pathname.startsWith('/programs') ||
    location.pathname.startsWith('/certifications') ||
    location.pathname.includes('/program') ||
    location.pathname.includes('/programs') ||
    location.pathname.includes('/certifications') ||
    location.pathname.includes('/overview') ||
    location.pathname.includes('/details')
  ) {
    return null;
  }

  const primaryLabel = isManage ? 'Staff Hub' : `${activeCert?.shortName || 'PREP'} Hub`;
  const primaryPageTitle = getPageTitle(pageKey);
  const deepLevelLabel = nesting?.level2Label;

  // Theme-specific style helpers
  const containerStyle = tokens.isStructuredCourse
    ? 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xs'
    : tokens.isWarmAcademic
    ? 'bg-[#F8F7F3] dark:bg-slate-900 border-[#E3DFD5] dark:border-slate-800 shadow-xs'
    : tokens.isClinical
    ? 'bg-[#0B2942] border-teal-500/20 text-white shadow-md'
    : tokens.isHighContrast
    ? 'bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
    : tokens.isMinimal
    ? 'bg-white border-slate-300 shadow-none'
    : tokens.isVibrant
    ? 'bg-white dark:bg-slate-900 border-violet-200 dark:border-violet-900 shadow-sm'
    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm';

  const homeIconStyle = tokens.isStructuredCourse
    ? 'text-[#1D4ED8]'
    : tokens.isWarmAcademic
    ? 'text-[#0D6E6E]'
    : tokens.isClinical
    ? 'text-teal-300'
    : tokens.isHighContrast
    ? 'text-black'
    : tokens.isVibrant
    ? 'text-violet-600 dark:text-violet-400'
    : 'text-indigo-500';

  const primaryLinkStyle = tokens.isStructuredCourse
    ? 'text-slate-700 dark:text-slate-200 hover:text-[#1D4ED8] dark:hover:text-blue-400'
    : tokens.isWarmAcademic
    ? 'text-[#2D3748] dark:text-slate-200 hover:text-[#0D6E6E] dark:hover:text-teal-400'
    : tokens.isClinical
    ? 'text-slate-200 hover:text-white'
    : tokens.isHighContrast
    ? 'text-black hover:bg-amber-100 font-black'
    : tokens.isVibrant
    ? 'text-violet-700 dark:text-violet-300 hover:text-violet-900'
    : 'text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400';

  const separatorStyle = tokens.isStructuredCourse
    ? 'text-slate-300 dark:text-slate-700'
    : tokens.isWarmAcademic
    ? 'text-[#A0AEC0] dark:text-slate-600'
    : tokens.isClinical
    ? 'text-teal-500/40'
    : tokens.isHighContrast
    ? 'text-black font-black'
    : 'text-slate-300 dark:text-slate-700';

  const baseTitleStyle = deepLevelLabel
    ? (tokens.isStructuredCourse
        ? 'text-slate-500 hover:text-[#1D4ED8] cursor-pointer'
        : tokens.isWarmAcademic 
        ? 'text-[#4A5568] dark:text-slate-300 hover:text-[#0D6E6E] dark:hover:text-teal-400 cursor-pointer' 
        : tokens.isClinical 
        ? 'text-slate-300 hover:text-white cursor-pointer' 
        : 'text-slate-500 hover:text-indigo-600 cursor-pointer')
    : (tokens.isStructuredCourse
        ? 'text-[#1D4ED8] font-extrabold cursor-default'
        : tokens.isWarmAcademic
        ? 'text-[#0D6E6E] dark:text-teal-300 font-extrabold cursor-default'
        : tokens.isClinical
        ? 'text-teal-300 font-serif font-bold cursor-default'
        : tokens.isHighContrast
        ? 'text-black font-black cursor-default'
        : 'text-slate-800 dark:text-white cursor-default');

  const deepBadgeStyle = tokens.isStructuredCourse
    ? 'bg-blue-50 dark:bg-blue-950 text-[#1D4ED8] dark:text-blue-300 border-blue-200 dark:border-blue-800'
    : tokens.isWarmAcademic
    ? 'bg-[#E6F4F1] text-[#0D6E6E] border-[#0D6E6E]/30 dark:bg-slate-800 dark:text-teal-300 dark:border-slate-700'
    : tokens.isClinical
    ? 'bg-[#0F7D6C] text-white border-teal-400/30'
    : tokens.isHighContrast
    ? 'bg-amber-300 text-black border-2 border-black font-black'
    : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-white border-slate-200 dark:border-slate-700';

  const exitBtnStyle = tokens.isStructuredCourse
    ? 'bg-[#1D4ED8] hover:bg-[#1E40AF] text-white border-blue-800/30 shadow-xs'
    : tokens.isWarmAcademic
    ? 'bg-[#E05638] hover:bg-[#C8472B] text-white border-[#C8472B]/30 shadow-xs'
    : tokens.isClinical
    ? 'bg-[#0F7D6C] hover:bg-[#0B6356] text-white border-teal-400/30'
    : tokens.isHighContrast
    ? 'bg-amber-400 hover:bg-amber-500 text-black border-2 border-black font-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]'
    : 'bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white shadow-xs';

  return (
    <nav 
      aria-label="Breadcrumb" 
      className={`mb-6 px-4 py-3 md:px-5 md:py-3.5 rounded-2xl border flex flex-wrap md:flex-nowrap justify-between items-center gap-4 transition-all duration-200 print:hidden ${containerStyle}`}
    >
      <ol className="flex items-center flex-wrap gap-1.5 text-[11px] md:text-xs font-bold">
        <li className="flex items-center">
          <button 
            onClick={handlePrimaryClick}
            className={`flex items-center gap-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-[#0D6E6E]/40 rounded px-1 py-0.5 leading-none ${primaryLinkStyle}`}
            title={`Go home to ${primaryLabel}`}
          >
            {isManage ? <Shield size={14} className="text-rose-500" /> : <Home size={14} className={homeIconStyle} />}
            <span className="hidden sm:inline font-extrabold uppercase tracking-wider text-[10px]">{primaryLabel}</span>
          </button>
        </li>
        
        <li className={`flex items-center select-none ${separatorStyle}`}>
          <ChevronRight size={14} strokeWidth={2.5} />
        </li>

        <li className="flex items-center">
          <button 
            onClick={deepLevelLabel ? triggerBackNavigation : handleBasePageClick}
            disabled={!deepLevelLabel}
            className={`transition-colors focus:outline-none focus:ring-2 focus:ring-[#0D6E6E]/40 rounded px-1 py-0.5 uppercase tracking-wider text-[10px] md:text-[11px] font-black ${baseTitleStyle}`}
          >
            {primaryPageTitle}
          </button>
        </li>

        {deepLevelLabel && (
          <>
            <li className={`flex items-center select-none ${separatorStyle}`}>
              <ChevronRight size={14} strokeWidth={2.5} />
            </li>
            <li className={`flex items-center max-w-[150px] md:max-w-xs truncate text-[10px] md:text-[11px] font-black px-2.5 py-1 rounded-md border leading-tight ${deepBadgeStyle}`}>
              {deepLevelLabel}
            </li>
          </>
        )}
      </ol>

      {/* High-contrast easily visible Back Button */}
      {deepLevelLabel && (
        <button
          onClick={triggerBackNavigation}
          className={`flex items-center gap-2 px-4 py-2 font-extrabold text-[10px] tracking-widest uppercase rounded-xl transition-all active:translate-y-px active:scale-95 group focus:outline-none shrink-0 ${exitBtnStyle}`}
          aria-label="Navigate Back to main category list"
        >
          <ArrowLeft size={14} strokeWidth={2.5} className="group-hover:-translate-x-0.5 transition-transform" />
          <span>Exit Lesson</span>
        </button>
      )}
    </nav>
  );
};

